import { FormAction } from "../formReducer";
import Ajv, { ErrorObject, ValidateFunction } from "ajv";
import addFormats from "ajv-formats";
import {
  FieldRecord,
  FormField,
  FormState,
  Form as FormData,
  FormPage,
  FormBlock,
  FormErrorMsg,
  DateField,
} from "../FormTypes";
import { hasConditionalLogic } from "./conditionalLogic";
import { ComponentMetaSchema } from "../BlockMetaSchemas";
import { isQuestionBlock } from "./typePredicates";
import format from "date-fns/format";

export type ValidationScope = "touched" | "all" | "page";

const SCHEMA_BASE = {
  type: "object",
  properties: {},
};

// provides an ISO date string with a zeroed out time value
// to hopefully make dealing with timezones easier.
const DATE_FORMAT_STRING = "yyyy-MM-dd'T'00:00:00.000'Z'";

// Derive the form schema from individual block schemas
export const buildFormSchema = (fields: FieldRecord) => {
  const [fieldSchemas, requiredFields] = Object.values(fields)
    .filter((f) => !!f.params.schema)
    .reduce(
      ([fs, rs]: [Record<string, unknown>, string[]], f) => {
        /** Merge property schemas and derive a list of required fields.
        Note that if a block has conditional logic it *can't* be 'required' in the schema,
        as JSON schema doesn't give us an easy & satisfactory way to mark a field as required
        only if it's shown.*/
        return [
          { ...fs, [f.field_key]: f.params.schema },
          f.required && !hasConditionalLogic(f)
            ? [...rs, f.field_key]
            : [...rs],
        ];
      },
      [{}, []]
    );

  return { ...SCHEMA_BASE, properties: fieldSchemas, required: requiredFields };
};

/** Process a field state object to get the current responses.
Extracted to a separate function so it can be used to validate URL prameters as well. */
const getResponsesFromFields = (
  fields: FieldRecord,
  forSubmission: boolean = false,
  forUrl: boolean = false
): { [k: string]: string | boolean | string[] | null } => {
  const data = Object.values(fields);

  if (forSubmission && data.some((f) => !f.writeToAttribute)) {
    const missingAttributes = data.filter((f) => !f.writeToAttribute);

    console.error(
      "The form could not be submitted; one or more fields are missing attributes: ",
      missingAttributes
    );

    throw new Error(FormErrorMsg.MISSING_TOKENS);
  }

  if (forUrl && data.some((f) => !f.params.url_name)) {
    const missingUrlNames = data.filter((f) => !f.params.url_name);

    console.error(
      "The form could not be prefilled; one or more fields are missing url names: ",
      missingUrlNames
    );

    throw new Error(FormErrorMsg.MISSING_TOKENS);
  }

  return data
    .filter(
      (
        field
      ): field is FormField & {
        currentValue: Date | string | Set<string>;
        field_key: string;
      } =>
        field.field_key !== null &&
        field.currentValue !== null &&
        field.currentValue !== undefined
    )
    .reduce((acc, field) => {
      let response: string | boolean | string[] | null;

      switch (field.input_type) {
        case "multi-select":
          response = Array.from(field.currentValue as Set<string>);
          break;
        case "date":
        case "dob":
          // date-fns `format` will error if passed an invalid date, so we need to check for that first and fall back to a string
          response = isNaN(field.currentValue.valueOf())
            ? field.currentValue.toString()
            : format(field.currentValue, DATE_FORMAT_STRING);
          break;
        default:
          response = field.currentValue;
          break;
      }

      let key;

      if (forSubmission) {
        key = field.writeToAttribute;
      } else if (forUrl) {
        key = field.params.url_name;
      } else {
        key = field.field_key;
      }

      return {
        ...acc,
        [key as string]: response,
      };
    }, {});
};

// Process a form state to extract response data for submission or validation
export const getSubmissionData = (
  { fields, userAgent, formId, formInstance }: FormState,
  forSubmission: boolean = false
) => {
  const responses = getResponsesFromFields(fields, forSubmission);

  return { ...responses, userAgent, formId, formInstance };
};

/**  Function to validate responses and dispatch a result.
The second argument is an object to indicate how much of the schema should be validated.
"touched" = Display errors for only the fields the user has interacted with.
"all" = Display errors for the entire form; for use before submission.
"page" = Display errors for the page the user is currently viewing; prevents the user from leaving a page with invalid data.
*/
export const validateResponses = (
  validator: ValidateFunction,
  validationScope:
    | {
        scope: "touched" | "all";
      }
    | { scope: "page"; pageIdx: number } = { scope: "touched" }
) => {
  return (
    dispatch: (action: FormAction) => void,
    getState: () => FormState
  ) => {
    const state = getState();
    const responses = getSubmissionData(state);

    // Mutability. GROSS.
    validator(responses);

    if (!validator.errors || !validator.errors.length) {
      return dispatch({
        type: "validate",
        result: null,
        ...validationScope,
      });
    }

    const errorMessages = getHumanErrors(validator.errors, state.fields);

    dispatch({
      type: "validate",
      result: errorMessages,
      ...validationScope,
    });
  };
};

// Convenience function to log an error report object in a readable format
const logFormattedErrorReport = (errors: ErrorReport, debug = false): void => {
  const logger = debug ? console.warn : console.error;

  if (debug) {
    logger(
      "Some fields on this form are hidden, but were not passed in the URL."
    );
  } else {
    logger(
      "The following issues were found when trying to fill responses from the URL parameters."
    );
  }
  Object.entries(errors).forEach(([param, errors]) => {
    const label = `%c\`${param}\`:`;
    logger(label, "font-weight: bold");

    errors.forEach((e: string) => logger(`  - ${e}`));
  });
};

/** Thunk action creator for validating responses passed as URL query parameters.
    Accepts the URL parameters
    */
export const validateURLParams = (urlMappings: Record<string, string>) => {
  return (
    dispatch: (action: FormAction) => void,
    getState: () => FormState
  ) => {
    const state = getState();

    // Build an object mapping url params to fields, so we can show error messages by URL param name
    const prefill_fields: { [urlParam: string]: FormField } = Object.values(
      state.fields
    ).reduce((acc, field) => {
      if (!field.attributes || field.attributes.length == 0) {
        return { ...acc };
      }

      // Check if one of the attributes for the field has a token that matches a url param
      const tokenForUrlParam = field.attributes.find(
        (a) => !!urlMappings[a.token]
      )?.token;

      if (tokenForUrlParam) {
        const urlParamName = urlMappings[tokenForUrlParam];

        return { ...acc, [urlParamName]: field };
      }
      return { ...acc };
    }, {});

    // create validator
    const ajv = new Ajv({ coerceTypes: true, allErrors: true });
    addFormats(ajv);

    // Build a schema from the fields that have url params
    const fieldSchemas = Object.entries(prefill_fields).reduce(
      (fs, [urlParam, field]) => {
        return { ...fs, [urlParam]: field.params.schema };
      },
      {}
    );
    const validator = ajv.compile({
      ...SCHEMA_BASE,
      properties: fieldSchemas,
      required: Object.keys(fieldSchemas),
    });

    // Build a response object for URL-param fields, so we can validate it
    const responses = getResponsesFromFields(prefill_fields, false, true);

    validator(responses);

    if (validator.errors) {
      // Transform AJV errors into a human-readable format
      const errorReport = getHumanErrors(validator.errors, prefill_fields);

      logFormattedErrorReport(errorReport, state.livePreview);

      // If we're not in live preview mode, dispatch the error report
      if (!state.livePreview) {
        dispatch({
          type: "validateUrlParams",
          result: errorReport,
        });
      }
    } else {
      dispatch({
        type: "validateUrlParams",
        result: {},
      });
    }
  };
};

/** Inner function for validating blocks; accepts an instance of AJV and the block to validate */
const _validateBlock = (
  ajv: Ajv,
  block: FormBlock
): ErrorObject<string, Record<string, any>, unknown>[] | null => {
  const blockMetaSchema = ComponentMetaSchema[block.block_type];

  if (blockMetaSchema === undefined) {
    console.error(
      "No params validation schema was found for this block: ",
      block
    );
  }

  ajv.validate(blockMetaSchema, block.params);

  if (ajv.errors !== null) {
    console.error("Invalid form block parameters found, for block: ", block);
    console.error("The following errors were found: ", ajv.errors);
  }

  return ajv.errors ?? null;
};

/** Thin wrapper around the private function above that initialises Ajv, as a convenience */
export const validateFormBlock = (
  block: FormBlock
): ErrorObject<string, Record<string, any>, unknown>[] | null => {
  const ajv = new Ajv({ coerceTypes: true, allErrors: true, $data: true });

  return _validateBlock(ajv, block);
};

/** To ensure that the hub isn't somehow passing a form definition (mainly block parameters and JSON schemas for now)
    that's invalid, or will lead to form responses that can't be captured, this function uses JSON schemas (meta-schemas?) to validate
    each block's parameters and schema definition.
 */
export const formDefinitionIsValid = (
  { pages }: FormData,
  previewMode: boolean
): boolean => {
  const ajv = new Ajv({ coerceTypes: true, allErrors: true, $data: true });

  const result = pages
    .flatMap((p: FormPage) => p.blocks)
    .every((b: FormBlock) => {
      const errors = _validateBlock(ajv, b);
      const attributeMissing =
        previewMode === false &&
        isQuestionBlock(b) &&
        (b.attributes ?? []).length === 0;

      if (attributeMissing) {
        console.error("No attribute token found for block: ", b);
      }

      return errors === null && !attributeMissing;
    });

  return result;
};

// Utility function to get the field key from an AJV error object
const getErrorFieldKey = (err: ErrorObject): string | null => {
  if (err.keyword === "required") {
    return err.params.missingProperty;
  } else {
    return err.instancePath !== ""
      ? err.instancePath.split("/").at(1) ?? null
      : null;
  }
};

// Function to convert Flatpickr date formatting tokens into Date-Fns formatting tokens
const getDateFnsFormatter = (flatPickrFormat: string) => {
  switch (flatPickrFormat) {
    case "m/d/Y":
      return "MM/dd/yyyy";
    case "Y/m/d":
      return "yyyy/MM/dd";
    case "d/m/Y":
    default:
      return "dd/MM/yyyy";
  }
};

// Generate human-readable error messages from AJV error objects
// Some messages should be swallowed; in these cases we return null
const getErrorMessage = (
  err: ErrorObject,
  field: FormField
): { key: string; msg: string | null } => {
  const key = getErrorFieldKey(err);

  // This should never happen. We should always be able to find a field from an error.
  // If it does, there's not much we can do but throw an error.
  if (!key) {
    const errorMsg = "Unable to get field key from error object.";

    console.error(errorMsg);
    throw new Error(errorMsg);
  }

  // Legal blocks are a special case; should return the same error for "required" or "const" errors
  if (field.block_type === "legal") {
    return {
      key,
      msg: "Confirmation is required to continue.",
    };
  }

  // Use AJV's default error message as a fallback if available; if that fails, use a generic message
  let msg: string | null =
    err.message ?? "The answer to this question is invalid.";

  switch (err.keyword) {
    case "required": {
      msg = "This question is required.";
      break;
    }
    case "format": {
      msg = `Must be a valid ${err.params.format}.`;
      break;
    }
    case "maxLength": {
      msg = `Must be no more than ${err.params.limit} characters.`;
      break;
    }
    case "minLength": {
      msg = `Must be at least ${err.params.limit} characters.`;
      break;
    }
    case "maxItems": {
      msg = `Choose no more than ${err.params.limit}.`;
      break;
    }
    case "minItems": {
      if (field.block_type === "ranking") {
        msg = "Please rank all options.";
      } else {
        msg = `Choose at least ${err.params.limit}.`;
      }
      break;
    }
    case "maximum":
    case "exclusiveMaximum":
    case "minimum":
    case "exclusiveMinimum": {
      let comparisonTerm: string = "";

      switch (err.params.comparison) {
        case ">=":
          comparisonTerm = "greater than or equal to";
          break;
        case ">":
          comparisonTerm = "greater than";
          break;
        case "<=":
          comparisonTerm = "less than or equal to";
          break;
        case "<":
          comparisonTerm = "less than";
          break;
      }

      msg = `Must be ${comparisonTerm} ${err.params.limit}.`;
      break;
    }

    case "formatMaximum":
    case "formatExclusiveMaximum":
    case "formatMinimum":
    case "formatExclusiveMinimum": {
      let comparisonTerm: string = "";
      const flatPickrFormat = (field as DateField).params.formatString;
      const dateFormat = flatPickrFormat
        ? getDateFnsFormatter(flatPickrFormat)
        : "MMMM do yyyy";

      const humanDate = format(new Date(err.params.limit), dateFormat);

      switch (err.params.comparison) {
        case ">=":
          comparisonTerm = "on or after";
          break;
        case ">":
          comparisonTerm = "after";
          break;
        case "<=":
          comparisonTerm = "on or before";
          break;
        case "<":
          comparisonTerm = "before";
          break;
      }

      msg = `Must be ${comparisonTerm} ${humanDate}.`;
      break;
    }
    case "maximum":
    case "exclusiveMaximum":
    case "minimum":
    case "exclusiveMinimum": {
      let comparisonTerm: string = "";

      switch (err.params.comparison) {
        case ">=":
          comparisonTerm = "greater than or equal to";
          break;
        case ">":
          comparisonTerm = "greater than";
          break;
        case "<=":
          comparisonTerm = "less than or equal to";
          break;
        case "<":
          comparisonTerm = "less than";
          break;
      }

      msg = `Must be ${comparisonTerm} ${err.params.limit}.`;
      break;
    }
    case "type": {
      switch (err.params.type) {
        case "string":
          msg = "Must be text.";
          break;
        case "integer":
          msg = "Must be a whole number.";
          break;
        case "number":
          msg = "Must be a number.";
          break;
      }
      break;
    }
    case "pattern": {
      switch (field.block_type) {
        case "phone":
          msg = "Must be a valid phone number.";
          break;
        case "multi_choice":
        case "dropdown":
          msg = "Must not be empty.";
          break;
        case "ranking":
          msg = "Please rank all options.";
          break;
        default:
          msg = "Does not match the expected format.";
          break;
      }
      break;
    }
    case "enum": {
      msg = "Must be one of the available options.";
      break;
    }
    case "anyOf": {
      msg = null;
      break;
    }
    default: {
      console.debug("No error message found for error: ", err);
      console.debug("Block type: ", field.block_type);
      break;
    }
  }

  return { key, msg };
};

export type ErrorReport = Record<string, string[]>;

/** Function to generate a human error message from AJV's validation errors
    Receives an AJV validation error object, returns a record mapping field keys to an array of human-readable error messages
 */
export const getHumanErrors = (
  errors: ErrorObject[],
  fields: Partial<Record<string, FormField>>
): ErrorReport => {
  return errors.reduce((acc, err) => {
    try {
      const errorKey: string | null = getErrorFieldKey(err);

      const field = (errorKey && fields[errorKey]) ?? null;

      // This should never happen. We should always be able to find a field from an error.
      // If it does, there's not much we can do but throw an error.
      if (!errorKey || !field) {
        const errorMsg =
          "No field found for error object: ${JSON.stringify(err)}";
        console.error(errorMsg);

        throw new Error(errorMsg);
      }

      // For each error, get the field key and generate a better error based on the output from AJV
      const { key, msg } = getErrorMessage(err, field);

      // `null` messages mean errors we don't want to show to the user; skip them
      if (msg === null) {
        return acc;
      }

      // Initialise the array of errors for this field if it doesn't exist yet
      // If the user has set a custom error message for this field, show it first
      let existing: string[] = [];

      if (acc[key]) {
        existing = acc[key];

        // Don't show custom error messages for legal blocks
      } else if (field.block_type !== "legal") {
        const customError = field.error;

        existing = customError ? [customError] : [];
      }

      return { ...acc, [key]: [...existing, msg] };
    } catch (e) {
      console.error("Error generating human error message: ", e);
      return acc;
    }
  }, {} as { [key: string]: string[] });
};

// Subset of the FormField type that contains only the properties relevant to validation
export type FieldValidationState = Pick<
  FormField,
  "status" | "validationErrors"
>;

/** Utility predicate function to determine if a validation error should be registered;
    based on the current validation scope **/
export const shouldRegisterError = (
  validationAction: { scope: ValidationScope; pageIdx?: number },
  suppressUrlValidation: boolean,
  field: FormField,
  error?: FieldValidationState
): boolean => {
  // No error, no worries
  if (!error) {
    return false;
  }

  // If this is a hidden field, and we're suppressing URL validation, don't register the error
  if (field.isPrefilled && suppressUrlValidation) {
    return false;
  }

  switch (validationAction.scope) {
    // Validate fields the user has touched
    case "touched": {
      return field.status !== "pending";
    }
    // Validate fields on the current page
    case "page": {
      return field.pageIdx === validationAction.pageIdx;
    }
    // Validate fields for the entire form
    case "all": {
      return true;
    }
  }
};
